import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { RingBadge } from "@/components/ui/RingBadge";
import type { Review, Suggestion, Suggestions, ToolVerdict } from "@/api/types";

// What a map suggests using, in the design's four labelled sections (docs/tools-catalog-
// design.md, "Suggestions in a capability map"). The backend composes and ranks them when the
// map is made; this only lays them out. Shared by the map result and the review workspace,
// which adds the reviewer's "will use" / "not a fit" buttons.

const HEADING = "mb-1.5 text-xs font-semibold tracking-wide text-text/60 uppercase";

const SECTIONS: { key: "required" | "delivers" | "may_help"; title: string; blurb: string }[] = [
  { key: "required", title: "Required by a standard", blurb: "Confirmed as the way to meet a standard on this map." },
  {
    key: "delivers",
    title: "Used to deliver this capability",
    blurb: "Confirmed as used for a capability this touches.",
  },
  {
    key: "may_help",
    title: "May help",
    blurb: "The model's picks and unconfirmed links — worth a look, not a requirement.",
  },
];

/** What the review workspace passes to add the reviewer's controls: its recorded call shows
 * as a badge, and while the review is open both buttons stay available (the current call
 * disabled) so a mistaken click can be changed. "Not a fit" asks for an optional reason. */
export interface SuggestionReviewing {
  review: Review;
  signedOff: boolean;
  onVerdict: (s: Suggestion, verdict: ToolVerdict, note?: string) => void;
}

/** A map's suggestions. Null `suggestions` (a map made before suggestions existed) shows a
 * one-line note; `reviewing` adds the review buttons. */
export function SuggestionsView({
  suggestions: s,
  reviewing,
}: {
  suggestions: Suggestions | null;
  reviewing?: SuggestionReviewing;
}) {
  if (!s) {
    return (
      <section>
        <h4 className={HEADING}>Suggested tools and practices</h4>
        <p className="text-sm text-text/60">This map was made before maps suggested tools.</p>
      </section>
    );
  }
  const empty = SECTIONS.every(({ key }) => s[key].length === 0) && s.gaps.length === 0 && s.warnings.length === 0;
  return (
    <section className="flex flex-col gap-3">
      <h4 className={HEADING}>Suggested tools and practices</h4>
      {empty && <p className="text-sm text-text/60">Nothing in the catalog was suggested for this change.</p>}

      {s.warnings.map((w) => (
        <p key={w.id + w.name} className="rounded border border-warn-line bg-warn-bg p-2 text-sm text-warn">
          {w.message}
        </p>
      ))}

      {SECTIONS.filter(({ key }) => s[key].length > 0).map(({ key, title, blurb }) => (
        <div key={key}>
          <p className="text-sm font-medium">{title}</p>
          <p className="mb-1.5 text-xs text-text/50">{blurb}</p>
          <ul className="flex flex-col gap-1.5">
            {s[key].map((e) => (
              <SuggestionRow key={`${e.type}:${e.id}`} entry={e} reviewing={reviewing} />
            ))}
          </ul>
        </div>
      ))}

      {s.gaps.length > 0 && (
        <div>
          <p className="text-sm font-medium">Nothing in the catalog covers</p>
          <ul className="list-disc pl-5 text-sm text-text/70">
            {s.gaps.map((g, i) => (
              <li key={i}>
                {g.need}
                {g.requirement && <span className="text-text/50"> — “{g.requirement}”</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** One suggestion: name (and feature), linking to its page, ring, kind, its caveat, why it's suggested, and the
 * review controls when reviewing. */
function SuggestionRow({ entry: e, reviewing }: { entry: Suggestion; reviewing?: SuggestionReviewing }) {
  const call = reviewing?.review.tool_reviews.find((t) => t.type === e.type && t.id === e.id);
  return (
    <li className="rounded border border-line p-2.5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to={`/${e.type === "tool" ? "tools" : "practices"}/${encodeURIComponent(e.id)}`}
          className="font-medium text-accent underline"
        >
          {e.name}
          {e.feature && ` ${e.feature.name}`}
        </Link>
        <RingBadge ring={e.feature?.ring ?? e.ring} />
        <span className="text-xs text-text/50">{e.kind ?? e.type}</span>
        {call && (
          <Badge tone={call.verdict === "will-use" ? "accent" : "warn"}>
            {call.verdict === "will-use" ? "will use" : "not a fit"}
          </Badge>
        )}
      </div>
      {e.caveat && <p className="mt-0.5 text-xs text-warn">{e.caveat}</p>}
      <ul className="mt-1 flex flex-col gap-0.5 text-text/70">
        {e.reasons.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>
      {call?.note && <p className="mt-1 text-xs text-text/60">Reviewer: {call.note}</p>}
      {reviewing && !reviewing.signedOff && (
        <div className="mt-2 flex gap-2">
          <Button onClick={() => reviewing.onVerdict(e, "will-use")} isDisabled={call?.verdict === "will-use"}>
            Will use
          </Button>
          <Button
            variant="danger"
            isDisabled={call?.verdict === "not-a-fit"}
            onClick={() => {
              const reason = window.prompt("Why isn't this a fit? (optional)");
              if (reason !== null) reviewing.onVerdict(e, "not-a-fit", reason);
            }}
          >
            Not a fit
          </Button>
        </div>
      )}
    </li>
  );
}
